import { Fragment, useContext, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Plus, Trash2 } from "lucide-react";
import CommonButton from "../../common/CommonButton";
import ModalCloseButton from "../../common/ModalCloseButton";
import ReactSelector from "../../common/CommonReactSelector";
import { components } from 'react-select';
import {
  getAllGeoCode,
  getSpecialAssign
} from "../../../service/Configuration";
import AppContext from "../../../context/AppContext";
import { toast } from "../../../utils/toast";
import { fetchFieldAgentMapProject } from "../../../service/Task";
import translations from "../../common/Translations";
import AssigneePicker, { emptySarRule, getRuleAssignScope } from "../../common/AssigneePicker";
import {
  currentUserCanUseGroups,
  loadGroupsForDepartment,
  normalizeGroups,
} from "../../task/GroupAssignParts";

const Placeholder = (props) => <components.Placeholder {...props} />;

// Backend validation failures (missing/foreign workflow, no workflow access,
// duplicate user+workflow rule) carry a readable message -- show it as-is.
const getBackendMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.header?.message ??
  error?.response?.data?.message;

export default function NewRuleModal({ newRule, setNewRule, workflowOptions = [], defaultWorkflowId = null, onSaved }) {
  const { projectId, langMode, selectedOrgId } = useContext(AppContext);
  // Each rule is scoped to one workflow; prefill with the table's workflow
  // filter when one is selected.
  const newSarRule = () => ({ ...emptySarRule(), workflowId: defaultWorkflowId });
  const [rules, setRules] = useState(() => [newSarRule()]);
  const [agentList, setAgentList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [GeoCodes, setGeoCodes] = useState([]);
  const [assign, setAssign] = useState(false);
  const [loadingGeoCodes, setLoadingGeoCodes] = useState(false);
  const canUseGroups = currentUserCanUseGroups();

  const groupOptions = (groups || []).map((g) => ({
    value: g.groupId,
    label: g.groupName,
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [geoData, agentData] = await Promise.all([
          getAllGeoCode(projectId),
          fetchFieldAgentMapProject(projectId)
        ]);

        setGeoCodes(Array.isArray(geoData) ? geoData : []);

        if (agentData?.body?.value) {
          const transformedAgents = agentData.body.value.map(agent => ({
            userId: agent.userId,
            userName: agent.userName,
          }));
          setAgentList(transformedAgents);
        } else {
          setAgentList([]);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoadingGeoCodes(false);
      }
    };

    setLoadingGeoCodes(true);
    fetchData();
  }, [projectId]);

  // The modal stays mounted, so re-seed the first rule each time it opens to
  // pick up the table's current workflow filter.
  useEffect(() => {
    if (newRule) setRules([newSarRule()]);
  }, [newRule]);

  useEffect(() => {
    const loadGroups = async () => {
      if (!canUseGroups) {
        setGroups([]);
        return;
      }
      const raw = await loadGroupsForDepartment();
      setGroups(normalizeGroups(raw));
    };
    loadGroups();
  }, [canUseGroups, selectedOrgId]);

  const handleChange = (index, field, selectedOptions, actionMeta) => {
    const updatedRules = [...rules];
    const ruleToUpdate = updatedRules[index];

    if (field === "userId" || field === "workflowId") {
      ruleToUpdate[field] = selectedOptions ? selectedOptions.value : null;
    } else if (field === "groupId") {
      ruleToUpdate[field] = selectedOptions ? selectedOptions.value : null;
      ruleToUpdate.groupName = selectedOptions ? selectedOptions.label : null;
    } else {
      const picked = selectedOptions || [];
      const hasAll = picked.some(opt => opt.value === "ALL");
      const wasAll = field === "areaCodes" && Boolean(ruleToUpdate.allAreaCodes);
      if (field === "areaCodes" && wasAll && hasAll) {
        // "All" is on, so every code shows ticked; unticking one code turns
        // "All" off and keeps the rest of the codes selected.
        const untickedCode = actionMeta?.option?.value ?? actionMeta?.removedValue?.value;
        if (untickedCode !== undefined && untickedCode !== "ALL") {
          ruleToUpdate.areaCodes = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(
            (code) => code && code !== untickedCode
          );
          ruleToUpdate.allAreaCodes = false;
        }
      } else {
        const pickedCodes = picked.filter(opt => opt.value !== "ALL").map(item => item.value);
        // Ticking every code by hand is the same as picking "All".
        const availableCodes = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(Boolean);
        const pickedEveryCode =
          field === "areaCodes" &&
          availableCodes.length > 0 &&
          availableCodes.every((code) => pickedCodes.includes(code));
        const isAllSelected = (hasAll && !wasAll) || pickedEveryCode;
        ruleToUpdate[field] = isAllSelected ? [] : pickedCodes;

        if (field === "areaCodes") {
          ruleToUpdate.allAreaCodes = isAllSelected;
        }
      }
    }
    setRules(updatedRules);
  };

  const setRuleAssignScope = (index, scope) => {
    const updatedRules = [...rules];
    const current = { ...updatedRules[index], assignScope: scope };
    if (scope === "groups") {
      current.userId = null;
    } else {
      current.groupId = null;
      current.groupName = null;
    }
    updatedRules[index] = current;
    setRules(updatedRules);
  };

  const handleAddRule = () => {
    setRules([...rules, newSarRule()]);
  };

  const handleRemoveRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
  };

  const handleSubmit = async () => {
    for (const rule of rules) {
      if (!rule.workflowId) {
        toast.error(translations[langMode].sarWorkflowRequired || "Please select a workflow");
        return;
      }
      const isGroupScope = canUseGroups && getRuleAssignScope(rule) === "groups";
      if (isGroupScope && !rule.groupId) {
        toast.error(translations[langMode].selectGroup || "Select Group");
        return;
      }
      if (!isGroupScope && !rule.userId) {
        toast.error(translations[langMode].selectFieldAget);
        return;
      }
    }

    const payload = {
      projectId,
      ruleList: rules.map((rule) => {
        const isGroupScope = canUseGroups && getRuleAssignScope(rule) === "groups";
        const { assignScope, groupName, ...rest } = rule;
        return {
          ...rest,
          workflowId: rule.workflowId,
          userId: isGroupScope ? null : rule.userId,
          groupId: isGroupScope ? rule.groupId : null,
          allAreaCodes: rule.allAreaCodes,
        };
      }),
      assign,
    };

    try {
      const result = await getSpecialAssign({ payload });
      if (result?.header?.code === 600) {
        toast.success(translations[langMode].ruleCreatedSuccessfully);
        hadleClose();
        onSaved?.();
      } else {
        toast.error(result?.body?.value || translations[langMode].failedtoassign);
      }
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error(getBackendMessage(error) || translations[langMode].failedtoassign);
    }
  };

  const hadleClose = () => {
    setNewRule(false);
    setRules([newSarRule()]);
    setAssign(false);
  };

  return (
    <Transition appear show={newRule} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={hadleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 z-0 flex items-center justify-center p-3 sm:p-4 overflow-visible">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl transform transition-all flex flex-col overflow-hidden"
              style={{ maxHeight: "85vh" }}
            >
              {/* Header, Assign All, and the footer are true shrink-0 flex
                  siblings (matching the prototype's NewSpecialRuleModal) --
                  only the rule-blocks region below scrolls, so they never
                  need "sticky" and can't get carried off-screen by a long
                  block list. */}
              <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100">
                <Dialog.Title className="text-base font-semibold text-gray-800 truncate">
                  {translations[langMode].newRule}
                </Dialog.Title>
                <ModalCloseButton onClick={hadleClose} />
              </div>

              <div className="shrink-0 flex items-center gap-3 px-4 sm:px-6 pt-4">
                <span className="text-sm font-semibold text-gray-800">
                  {translations[langMode].assignAll}
                </span>
                <button
                  onClick={() => setAssign(prev => !prev)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${assign ? "bg-teal-500 text-white" : "bg-gray-100 text-gray-500"}`}
                >
                  {assign ? translations[langMode].assignedAll : translations[langMode].notAssignedAll}
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                <div className="flex flex-col gap-4">
                    {rules.map((rule, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4"
                      >
                        {rules.length > 1 && (
                          <div className="flex justify-end -mt-1 -mr-1">
                            <button
                              className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                              title={translations[langMode].delete || "Delete"}
                              onClick={() => handleRemoveRule(index)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                        <div className="flex flex-col gap-1.5 w-full">
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                            {translations[langMode].sarWorkflow || "Workflow"} <span className="text-rose-500">*</span>
                          </label>
                          <ReactSelector
                            options={workflowOptions}
                            value={
                              rule.workflowId
                                ? workflowOptions.find((o) => o.value === rule.workflowId) || null
                                : null
                            }
                            onChange={(selected) => handleChange(index, "workflowId", selected)}
                            components={{ Placeholder }}
                            placeholder={translations[langMode].selectWorkflow || "Select Workflow"}
                            isSearchable
                            isClearable={false}
                            height="46px"
                            menuPortalTarget={document.body}
                            overflow="overlay"
                          />
                        </div>
                        <AssigneePicker
                          scope={getRuleAssignScope(rule)}
                          onScopeChange={(scope) => setRuleAssignScope(index, scope)}
                          langMode={langMode}
                          fieldAgent={agentList}
                          groupOptions={groupOptions}
                          userValue={
                            rule.userId
                              ? { value: rule.userId, label: agentList.find((a) => a.userId === rule.userId)?.userName }
                              : null
                          }
                          onUserChange={(selected) => handleChange(index, "userId", selected)}
                          groupValue={rule.groupId}
                          groupLabel={rule.groupName}
                          onGroupChange={(selected) => handleChange(index, "groupId", selected)}
                        />
                        {/* Same label + full-width control shape AssigneePicker
                            renders above, so Geo Codes lines up with the Assign
                            To selector instead of sitting inset in its own card. */}
                        <div className="flex flex-col gap-1.5 w-full">
                          <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                            {translations[langMode].geoCodes}
                          </label>
                          <ReactSelector
                            options={(() => {
                              const codeOptions = (Array.isArray(GeoCodes) ? GeoCodes : []).filter(Boolean).map(code => ({ value: code, label: code }));
                              return codeOptions.length ? [{ value: "ALL", label: "All" }, ...codeOptions] : [];
                            })()}
                            noOptionsMessage={() => translations[langMode]?.nodatavailvle ?? "No data available"}
                            onChange={(selected, meta) => handleChange(index, "areaCodes", selected, meta)}
                            isOptionSelected={(option, selectValue) =>
                              Boolean(rule.allAreaCodes) ||
                              (selectValue || []).some((item) => item.value === option.value)
                            }
                            closeMenuOnSelectValue="ALL"
                            components={{ Placeholder }}
                            placeholder={loadingGeoCodes ? translations[langMode].LoadingGeo : "Select..."}
                            isLoading={loadingGeoCodes}
                            height="46px"
                            isMulti
                            isClearable={false}
                            overflow="overlay"
                            selectedValuesMaxHeight="64px"
                            value={
                              rule.allAreaCodes
                                ? [{ value: "ALL", label: "All" }]
                                : (rule.areaCodes || []).map(code => ({ value: code, label: code }))
                            }
                            menuPortalTarget={document.body}
                          />
                        </div>
                        <div className="flex gap-2 justify-start items-center">
                          <span className="text-sm font-semibold text-gray-900">
                            {translations[langMode].Status}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={rule.active}
                            onClick={() => {
                              const updatedRules = [...rules];
                              updatedRules[index].active = !rule.active;
                              setRules(updatedRules);
                            }}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#236DB4] ${rule.active ? "" : "bg-gray-200"}`}
                            style={rule.active ? { background: "#188A4B" } : undefined}
                          >
                            {/* translate-x-* is a physical shift, so under dir="rtl"
                                the knob starts at the pill's right edge and gets
                                pushed further right -- out of the pill and over the
                                Status label. The rtl: variants mirror it. */}
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${rule.active ? "translate-x-6 rtl:-translate-x-6" : "translate-x-1 rtl:-translate-x-1"}`}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>

                <button
                  onClick={handleAddRule}
                  className="flex items-center gap-2 text-sm font-semibold text-[#236DB4] hover:text-[#1d5a96] transition-colors"
                >
                  <Plus size={16} /> {translations[langMode].addRule}
                </button>
              </div>

              <div className="shrink-0 flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-100">
                <CommonButton colorIntent="danger"
                  variant="danger"
                  onClick={hadleClose}
                >
                  {translations[langMode].cancle}
                </CommonButton>
                <CommonButton colorIntent="success"
                  onClick={handleSubmit}
                  variant="primary"
                >
                  {translations[langMode].ok}
                </CommonButton>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
